/* eslint-disable default-case */
import React, { useState, useMemo } from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import el from 'date-fns/locale/el';
import 'react-datepicker/dist/react-datepicker.css';
import { X, Check, Info, AlertTriangle } from 'lucide-react';
import { 
  getLeaveYear, 
  getUsedDaysByLeaveYear, 
  getYearlyBalance,
} from '../utils/vacationUtils';

registerLocale('el', el);

const LEAVE_TYPES = [
  { value: 'vacation',    label: 'Κανονική Άδεια',             icon: '🏖️' },
  { value: 'unjustified', label: 'Α.Α. - Αδικαιολόγητη Απουσία', icon: '⚠️' },
  { value: 'maternity',   label: 'Μητρότητα',                    icon: '🤱', gender: 'female' },
  { value: 'paternity',   label: 'Πατρότητα',                    icon: '👶', gender: 'male'   },
  { value: 'unpaid',      label: 'Άνευ Αποδοχών',                icon: '💼' },
];

const toLocalDateString = (date) => {
  if (!date) return '';
  const d = new Date(date);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const RequestModal = ({
  onSubmit,
  onClose,
  currentUser,
  existingRequests = [],
  holidays = [],
  annualBalances = [],
}) => {
  const [startDate, setStartDate] = useState(null);
  const [endDate,   setEndDate]   = useState(null);
  const [reason,    setReason]    = useState('');
  const [leaveType, setLeaveType] = useState('vacation');

  const currentCycleYear = useMemo(() => getLeaveYear(new Date()), []);
  const nextCycleYear    = currentCycleYear + 1;

  const isManager = useMemo(() => {
    if (!currentUser) return false;
    const role  = (currentUser.role || '').toLowerCase();
    const level = Number(currentUser.manager_level || currentUser.managerLevel || 0);
    return role === 'manager' || role === 'admin' || level > 0;
  }, [currentUser]);

  const isDateSelectable = (date) => {
    if (isManager) return true;
    const d = new Date(date); d.setHours(0, 0, 0, 0);
    const t = new Date();     t.setHours(0, 0, 0, 0);
    return d >= t;
  };

  const availableLeaveTypes = LEAVE_TYPES.filter(type => {
    if (!type.gender) return true;
    if (!currentUser?.gender) return true;
    return type.gender === currentUser.gender;
  });

  const getAllowanceForYear = (yr) => {
    if (!annualBalances || !annualBalances.length) return 0;
    const pivoted = annualBalances.find(b => Number(b.userId || b.user_id) === Number(currentUser.id));
    if (pivoted) {
      const v = pivoted[`balance_${yr}`];
      if (v !== undefined && v !== null) return Number(v);
    }
    return getYearlyBalance(currentUser.id, annualBalances, yr);
  };

  const prevYear = currentCycleYear - 1;
  const prevAllowed = getAllowanceForYear(prevYear);
  const prevRawUsed = useMemo(() => 
    getUsedDaysByLeaveYear(currentUser.id, existingRequests, prevYear, holidays),
    [currentUser.id, existingRequests, prevYear, holidays]
  );
  const overflowFromPrev = prevAllowed > 0 ? Math.max(0, prevRawUsed - prevAllowed) : 0;

  const currentAllowed = getAllowanceForYear(currentCycleYear);
  const currentRawUsed = useMemo(() =>
    getUsedDaysByLeaveYear(currentUser.id, existingRequests, currentCycleYear, holidays),
    [currentUser.id, existingRequests, currentCycleYear, holidays]
  );
  
  const currentUsed = currentAllowed > 0 ? Math.min(currentRawUsed, currentAllowed) : currentRawUsed;
  const overflowFromCurrent = currentAllowed > 0 ? Math.max(0, currentRawUsed - currentAllowed) : 0;
  const currentRemaining = Math.max(0, currentAllowed - currentUsed - overflowFromPrev);

  const nextAllowed = getAllowanceForYear(nextCycleYear);
  const nextRawUsed = useMemo(() =>
    getUsedDaysByLeaveYear(currentUser.id, existingRequests, nextCycleYear, holidays),
    [currentUser.id, existingRequests, nextCycleYear, holidays]
  );
  const nextUsed = nextAllowed > 0 ? Math.min(nextRawUsed, nextAllowed) : nextRawUsed;
  const nextRemaining = Math.max(0, nextAllowed - nextUsed - overflowFromCurrent);

  const totalRemaining = currentRemaining + nextRemaining;

  const requestedDays = useMemo(() => {
    if (!startDate || !endDate || leaveType !== 'vacation') return 0;

    let count = 0;
    let cur = new Date(startDate);
    const end = new Date(endDate);

    const existingTakenDates = new Set(
      existingRequests
        .filter(req => {
          const uid = req.user_id || req.userId;
          return uid === currentUser.id && ['approved', 'pending', 'manager1_approved', 'granted'].includes(req.status);
        })
        .flatMap(req => {
          const dates = [];
          let d = new Date(req.start_date || req.startDate);
          const e = new Date(req.end_date || req.endDate);
          while (d <= e) {
            dates.push(toLocalDateString(d));
            d.setDate(d.getDate() + 1);
          }
          return dates;
        })
    );

    const holidayStrings = new Set(holidays.map(h => {
        const d = h.holiday_date ? new Date(h.holiday_date) : new Date(h);
        return toLocalDateString(d);
    }));

    while (cur <= end) {
      const dateStr = toLocalDateString(cur);
      const dayOfWeek = cur.getDay();
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
      const isHoliday = holidayStrings.has(dateStr);
      const isTaken   = existingTakenDates.has(dateStr);

      if (!isWeekend && !isHoliday && !isTaken) {
        count++;
      }
      cur.setDate(cur.getDate() + 1);
    }
    return count;
  }, [startDate, endDate, leaveType, holidays, existingRequests, currentUser.id]);

  const fromCurrentCycle = Math.min(requestedDays, currentRemaining);
  const fromNextCycle    = Math.max(0, requestedDays - currentRemaining);
  const willOverflow     = fromNextCycle > 0;
  const notEnoughEven    = requestedDays > totalRemaining && leaveType === 'vacation';

  const checkDateOverlap = (s, e) => {
    const active = existingRequests.filter(req => {
      const uid = req.user_id || req.userId;
      return uid === currentUser.id && 
        ['approved', 'pending', 'manager1_approved', 'granted'].includes(req.status);
    });
    for (const req of active) {
      const es = new Date(req.startDate || req.start_date);
      const ee = new Date(req.endDate   || req.end_date);
      if ((s >= es && s <= ee) || (e >= es && e <= ee) || (s <= es && e >= ee)) {
        return { hasOverlap: true, conflictingRequest: req };
      }
    }
    return { hasOverlap: false };
  };

  const handleSubmit = () => {
    if (!startDate || !endDate || !reason || !leaveType) {
      alert('Παρακαλώ συμπληρώστε όλα τα πεδία');
      return;
    }
    if (requestedDays === 0 && leaveType === 'vacation') {
      alert('Οι επιλεγμένες εργάσιμες ημέρες είναι ήδη καλυμμένες από αργίες ή υπάρχουσες άδειες.');
      return;
    }
    const start = new Date(startDate);
    const end   = new Date(endDate);
    const today = new Date(); today.setHours(0, 0, 0, 0);

    if (!isManager && start < today) {
      alert('Η ημερομηνία έναρξης δεν μπορεί να είναι στο παρελθόν.');
      return;
    }
    const overlap = checkDateOverlap(start, end);
    if (overlap.hasOverlap) {
      alert('Υπάρχει ήδη αίτηση για αυτές τις ημερομηνίες.');
      return;
    }
    if (notEnoughEven) {
      alert(`Δεν έχετε αρκετές ημέρες. Διαθέσιμες: ${totalRemaining}`);
      return;
    }

    onSubmit({
      startDate: toLocalDateString(start),
      endDate:   toLocalDateString(end),
      reason,
      leaveType,
      leaveYear: currentCycleYear,
    });
    onClose();
  };

  const getDisabledDates = () => {
    return existingRequests
      .filter(req => {
        const uid = req.user_id || req.userId;
        return uid === currentUser.id && 
          ['approved', 'pending', 'manager1_approved', 'granted'].includes(req.status);
      })
      .flatMap(req => {
        const dates = [];
        for (let d = new Date(req.startDate || req.start_date); d <= new Date(req.endDate || req.end_date); d.setDate(d.getDate() + 1)) {
          dates.push(new Date(d));
        }
        return dates;
      });
  };

  if (!currentUser) return null;

  const currentPct  = currentAllowed > 0 ? Math.min(100, ((currentUsed + overflowFromPrev) / currentAllowed) * 100) : 0;
  const nextPct     = nextAllowed > 0 ? Math.min(100, (nextUsed / nextAllowed) * 100) : 0;
  const overflowPct = nextAllowed > 0 ? Math.min(100, (fromNextCycle / nextAllowed) * 100) : 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-jewel-bg rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-jewel-text">Ζητήστε Άδεια</h3>
          <button onClick={onClose} className="text-gray-400 dark:text-jewel-text-muted hover:text-gray-600 dark:hover:text-jewel-text">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-jewel-text mb-2">Τύπος Άδειας</label>
            <div className="grid grid-cols-2 gap-2">
              {availableLeaveTypes.map(type => (
                <button
                  key={type.value}
                  onClick={() => setLeaveType(type.value)}
                  className={`p-3 rounded-lg border-2 text-center transition-all ${
                    leaveType === type.value
                      ? 'border-blue-500 bg-blue-50 dark:bg-jewel-teal/20 text-blue-700 dark:text-jewel-teal-tint'
                      : 'border-gray-100 dark:border-jewel-border hover:border-gray-200 dark:hover:border-gray-600 bg-gray-50 dark:bg-jewel-bg/50 text-gray-700 dark:text-jewel-text'
                  }`}
                >
                  <div className="text-2xl mb-1">{type.icon}</div>
                  <div className="text-xs font-bold">{type.label}</div>
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-jewel-text mb-2">Από</label>
              <DatePicker
                selected={startDate}
                onChange={setStartDate}
                selectsStart startDate={startDate} endDate={endDate}
                filterDate={isDateSelectable}
                excludeDates={getDisabledDates()}
                locale="el" dateFormat="dd/MM/yyyy"
                placeholderText="Επιλέξτε ημερομηνία"
                className="w-full px-3 py-2 border border-gray-300 dark:border-jewel-border bg-white dark:bg-jewel-surface text-gray-900 dark:text-jewel-text rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-jewel-text mb-2">Έως</label>
              <DatePicker
                selected={endDate}
                onChange={setEndDate}
                selectsEnd startDate={startDate} endDate={endDate}
                minDate={startDate || new Date()}
                excludeDates={getDisabledDates()}
                locale="el" dateFormat="dd/MM/yyyy"
                placeholderText="Επιλέξτε ημερομηνία"
                className="w-full px-3 py-2 border border-gray-300 dark:border-jewel-border bg-white dark:bg-jewel-surface text-gray-900 dark:text-jewel-text rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-jewel-text mb-2">Αιτιολογία</label>
            <textarea
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="Γράψτε μια αιτιολογία..."
              className="w-full px-3 py-2 border border-gray-300 dark:border-jewel-border bg-white dark:bg-jewel-surface text-gray-900 dark:text-jewel-text rounded-lg focus:ring-2 focus:ring-blue-500 h-20 resize-none"
            />
          </div>
          {leaveType === 'vacation' && (
            <div className="rounded-lg border border-blue-100 dark:border-jewel-teal bg-blue-50 dark:bg-jewel-teal/20 p-3 space-y-3">
              <div className="flex items-center space-x-2">
                <Info className="h-3.5 w-3.5 text-blue-500 dark:text-jewel-teal-tint flex-shrink-0" />
                <span className="text-[11px] text-blue-700 dark:text-jewel-teal-tint font-bold uppercase tracking-wide">Υπόλοιπο Αδειών</span>
              </div>
              {/* Progress Bar for Current Cycle */}
              <div>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-gray-600 dark:text-jewel-text font-medium">Τρέχων κύκλος ({currentCycleYear}–{currentCycleYear + 1})</span>
                  <span className="font-bold text-blue-700 dark:text-jewel-teal-tint">{currentRemaining} / {currentAllowed} διαθ.</span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-jewel-surface rounded-full h-2 overflow-hidden">
                  <div className="h-2 flex rounded-full overflow-hidden">
                    <div className="h-2 bg-blue-400 transition-all duration-300" style={{ width: `${currentPct}%` }} />
                    {requestedDays > 0 && fromCurrentCycle > 0 && (
                      <div className="h-2 bg-orange-400 transition-all duration-300" style={{ width: `${currentAllowed > 0 ? Math.min(100 - currentPct, (fromCurrentCycle / currentAllowed) * 100) : 0}%` }} />
                    )}
                  </div>
                </div>
                <div className="flex justify-between text-[10px] text-gray-400 dark:text-jewel-text-muted mt-0.5">
                  <span>{currentUsed} χρησιμ. {overflowFromPrev > 0 && `(-${overflowFromPrev} από πέρυσι)`}</span>
                  {requestedDays > 0 && <span className="text-orange-500 dark:text-orange-400 font-medium">+{requestedDays} αυτή η αίτηση</span>}
                </div>
              </div>
              {/* Progress Bar for Next Cycle */}
              {(willOverflow || nextAllowed > 0) && (
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-gray-600 dark:text-jewel-text font-medium">Επόμενος κύκλος ({nextCycleYear}–{nextCycleYear + 1})</span>
                    <span className="font-bold text-purple-700 dark:text-jewel-ruby-tint">{nextRemaining} / {nextAllowed} διαθ.</span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-jewel-surface rounded-full h-2 overflow-hidden">
                    <div className="h-2 flex rounded-full overflow-hidden">
                      <div className="h-2 bg-purple-400 transition-all duration-300" style={{ width: `${nextPct}%` }} />
                      {willOverflow && <div className="h-2 bg-orange-400 transition-all duration-300" style={{ width: `${overflowPct}%` }} />}
                    </div>
                  </div>
                  <div className="flex justify-between text-[10px] text-gray-400 dark:text-jewel-text-muted mt-0.5">
                    <span>{nextUsed} χρησιμ.</span>
                    {willOverflow && <span className="text-orange-500 dark:text-orange-400 font-medium">+{fromNextCycle} από επόμενο κύκλο</span>}
                  </div>
                </div>
              )}
              {/* Warning Messages */}
              {willOverflow && !notEnoughEven && (
                <div className="flex items-start space-x-2 bg-orange-50 dark:bg-orange-900/30 border border-orange-200 dark:border-orange-800 rounded p-2">
                  <AlertTriangle className="h-3.5 w-3.5 text-orange-500 dark:text-orange-400 flex-shrink-0 mt-0.5" />
                  <p className="text-[11px] text-orange-700 dark:text-orange-300">
                    <strong>{fromCurrentCycle}</strong> ημ. από τρέχοντα κύκλο + <strong>{fromNextCycle}</strong> ημ. από επόμενο κύκλο.
                  </p>
                </div>
              )}
              {notEnoughEven && (
                <div className="flex items-start space-x-2 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded p-2">
                  <AlertTriangle className="h-3.5 w-3.5 text-red-500 dark:text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-[11px] text-red-700 dark:text-red-300">Ανεπαρκές υπόλοιπο και στους δύο κύκλους. Διαθέσιμες: {totalRemaining} ημέρες.</p>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="flex space-x-3 mt-6">
          <button onClick={onClose} className="flex-1 px-4 py-3 border border-gray-200 dark:border-jewel-border text-gray-600 dark:text-jewel-text rounded-lg hover:bg-gray-50 dark:hover:bg-jewel-surface font-medium transition-colors">
            Ακύρωση
          </button>
          <button
            onClick={handleSubmit}
            disabled={notEnoughEven}
            className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 shadow-lg transition-all active:scale-95 flex items-center justify-center space-x-2 font-bold disabled:bg-gray-300 dark:disabled:bg-gray-700 disabled:cursor-not-allowed"
          >
            <Check className="h-4 w-4" />
            <span>Υποβολή Αίτησης</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default RequestModal;